'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { downloadCsv } from '@/lib/csv';
import Link from 'next/link';
import { api } from '@/lib/api';
import { useAppStore } from '@/store/app';
import { translate } from '@/lib/i18n';
import { formatPrice, formatVolume } from '@/lib/format';
import { useDataSettings, formatTimeInTz } from '@/lib/settings';
import { useRequireAuth } from '@/lib/useRequireAuth';
import DataQualityBadge from '@/components/research/DataQualityBadge';
import EventPing from '@/components/EventPing';
import { ListSkeleton } from '@/components/Skeleton';
import EmptyState from '@/components/EmptyState';
import { buildCoinRoute, parseInstrumentId, type InstrumentIdentity } from '@/lib/instrument';
import TermTip from '@/components/TermTip';
import { signalPlain, type TermLang } from '@/lib/signalPlain';

interface SnapMetric {
  value: number;
  unit: string;
  window?: string;
}

interface MarketSignal {
  id: string;
  type: string;
  symbol: string;
  price: number;
  /** D05: alias of trigger-time price — cards label it 触发价. */
  triggerPrice?: number;
  /** D05: live price at read time (from ticker cache), labelled 现价 — never overrides triggerPrice. */
  currentPrice?: number;
  changePct: number;
  volumeUsd?: number;
  volume5mUsd?: number;
  fundingRate?: number;
  oiChangePct?: number;
  detail: string;
  time: number;
  /** AUD07 frozen snapshot fields. */
  snapshotId?: string;
  narrative?: string;
  metricValues?: Record<string, SnapMetric>;
  thresholds?: Record<string, { operator: string; value: number; unit: string }>;
  venue?: string;
  product?: string;
  instrumentId?: string;
  availableAt?: number;
  ruleVersion?: number;
  source?: string;
  quality?: string;
  _fav?: boolean;
  _note?: string;
}

interface SignalListResponse {
  items: MarketSignal[];
  nextCursor: string | null;
  total: number;
  counts?: Record<string, number>;
}

type SignalCategory = 'all' | 'liquidation' | 'funding' | 'volume' | 'technical' | 'price' | 'oi' | 'orderbook';

const TYPE_META: Record<string, { label: string; en: string; cls: string; cat: SignalCategory }> = {
  // Price
  price_spike: { label: '价格飙升', en: 'Price spike', cls: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30', cat: 'price' },
  price_drop: { label: '价格暴跌', en: 'Price drop', cls: 'bg-red-500/15 text-red-400 border-red-500/30', cat: 'price' },
  breakout_high: { label: '突破新高', en: 'Breakout high', cls: 'bg-lime-500/15 text-lime-400 border-lime-500/30', cat: 'price' },
  breakout_low: { label: '跌破新低', en: 'Breakdown low', cls: 'bg-rose-500/15 text-rose-400 border-rose-500/30', cat: 'price' },
  // Volume
  volume_spike: { label: '量能激增', en: 'Volume surge', cls: 'bg-sky-500/15 text-sky-400 border-sky-500/30', cat: 'volume' },
  large_trade: { label: '大单成交', en: 'Large trade', cls: 'bg-yellow-500/15 text-yellow-400 border-yellow-500/30', cat: 'volume' },
  // Liquidation
  liq_surge: { label: '爆仓激增', en: 'Liq. surge', cls: 'bg-orange-500/15 text-orange-400 border-orange-500/30', cat: 'liquidation' },
  long_liq_cascade: { label: '多单爆仓', en: 'Long liq. cascade', cls: 'bg-red-600/20 text-red-300 border-red-500/40', cat: 'liquidation' },
  short_liq_cascade: { label: '空单爆仓', en: 'Short liq. cascade', cls: 'bg-emerald-600/20 text-emerald-300 border-emerald-500/40', cat: 'liquidation' },
  liq_cluster_near: { label: '接近历史爆仓密集区', en: 'Near liq. cluster', cls: 'bg-red-500/15 text-red-300 border-red-500/30', cat: 'liquidation' },
  // Funding / LSR
  funding_extreme: { label: '资金费率', en: 'Funding extreme', cls: 'bg-amber-500/15 text-amber-400 border-amber-500/30', cat: 'funding' },
  funding_reversal: { label: '费率反转', en: 'Funding reversal', cls: 'bg-amber-400/15 text-amber-300 border-amber-400/30', cat: 'funding' },
  leverage_surge: { label: '杠杆飙升', en: 'Leverage surge', cls: 'bg-fuchsia-400/15 text-fuchsia-300 border-fuchsia-400/30', cat: 'funding' },
  lsr_extreme: { label: '多空比极端', en: 'Long/short extreme', cls: 'bg-fuchsia-500/15 text-fuchsia-400 border-fuchsia-500/30', cat: 'funding' },
  basis_spread: { label: '期现价差', en: 'Basis spread', cls: 'bg-cyan-500/15 text-cyan-400 border-cyan-500/30', cat: 'funding' },
  // OI
  oi_spike: { label: '持仓激增', en: 'OI spike', cls: 'bg-violet-500/15 text-violet-400 border-violet-500/30', cat: 'oi' },
  oi_drop: { label: '持仓萎缩', en: 'OI drop', cls: 'bg-purple-500/15 text-purple-400 border-purple-500/30', cat: 'oi' },
  oi_price_divergence: { label: '持仓背离', en: 'OI-price divergence', cls: 'bg-indigo-500/15 text-indigo-400 border-indigo-500/30', cat: 'oi' },
  // Technical
  rsi_overbought: { label: 'RSI超买', en: 'RSI overbought', cls: 'bg-red-500/15 text-red-400 border-red-500/30', cat: 'technical' },
  rsi_oversold: { label: 'RSI超卖', en: 'RSI oversold', cls: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30', cat: 'technical' },
  volatility_surge: { label: '波动率飙升', en: 'Volatility surge', cls: 'bg-pink-500/15 text-pink-400 border-pink-500/30', cat: 'technical' },
  macd_bearish_divergence: { label: 'MACD顶背离', en: 'MACD bearish div.', cls: 'bg-red-400/15 text-red-300 border-red-400/30', cat: 'technical' },
  macd_bullish_divergence: { label: 'MACD底背离', en: 'MACD bullish div.', cls: 'bg-emerald-400/15 text-emerald-300 border-emerald-400/30', cat: 'technical' },
  macd_golden_cross: { label: 'MACD金叉', en: 'MACD golden cross', cls: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40', cat: 'technical' },
  macd_death_cross: { label: 'MACD死叉', en: 'MACD death cross', cls: 'bg-red-500/20 text-red-300 border-red-500/40', cat: 'technical' },
  // Orderbook P1
  orderbook_imbalance: { label: '盘口失衡', en: 'Orderbook imbalance', cls: 'bg-teal-500/15 text-teal-400 border-teal-500/30', cat: 'orderbook' },
  depth_drop: { label: '深度骤降', en: 'Depth drop', cls: 'bg-orange-400/15 text-orange-300 border-orange-400/30', cat: 'orderbook' },
};

const CATEGORIES: Array<{ key: SignalCategory; label: string; en: string }> = [
  { key: 'all', label: '全部', en: 'All' },
  { key: 'liquidation', label: '爆仓', en: 'Liquidation' },
  { key: 'funding', label: '资金/多空', en: 'Funding/LSR' },
  { key: 'volume', label: '量能/大单', en: 'Volume/Whale' },
  { key: 'oi', label: '持仓', en: 'OI' },
  { key: 'technical', label: '技术指标', en: 'Technical' },
  { key: 'orderbook', label: '盘口', en: 'Orderbook' },
  { key: 'price', label: '价格', en: 'Price' },
];

const VENUES = ['', 'binance', 'okx', 'gate', 'bitget'];

/** AUD03/R04: metric-aware formatting — metric semantics first, unit as fallback.
 *  RSI is unitless 0-100 (never %), LSR/leverage are ratios (x), only real
 *  percent metrics carry % (change_pct/funding_rate/volatility/OI…). */
function fmtMetricValue(type: string, metricId: string, v: SnapMetric): string {
  if (metricId === 'rsi') return v.value.toFixed(1);
  if (metricId === 'lsr' || metricId === 'lsr_lo' || metricId === 'leverage' || metricId === 'ratio' || metricId === 'volume_ratio') return `${v.value.toFixed(2)}x`;
  const unit = v.unit;
  if (unit === 'percent') return `${Number(v.value.toFixed(4))}%`;
  if (unit === 'rate') return fmtRatePct(v.value);
  if (unit === 'usd') return formatVolume(v.value);
  if (unit === 'price') return formatPrice(v.value);
  if (unit === 'unitless' || unit === 'pp' || unit === 'count') return String(Number(v.value.toFixed(1)));
  if (unit === 'ratio') return `${v.value.toFixed(2)}x`;
  if (metricId === 'funding_rate' || metricId === 'change_pct') return `${Number(v.value.toFixed(4))}%`;
  return String(Number(v.value.toFixed(4)));
}

function timeAgo(ts: number, lang: string): string {
  const s = Math.max(0, Math.floor((Date.now() - ts) / 1000));
  if (s < 60) return lang !== 'en' ? '刚刚' : 'just now';
  if (s < 3600) return lang !== 'en' ? `${Math.floor(s / 60)} 分钟前` : `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return lang !== 'en' ? `${Math.floor(s / 3600)} 小时前` : `${Math.floor(s / 3600)}h ago`;
  return lang !== 'en' ? `${Math.floor(s / 86400)} 天前` : `${Math.floor(s / 86400)}d ago`;
}

/** R04: threshold unit by metric semantics — RSI unitless (never "pp"),
 *  ratios get "x", percent gets "%", USD/price unitless, others raw. */
function fmtThresholdUnit(k: string, unit: string): string {
  if (k === 'rsi') return '';
  if (k === 'lsr' || k === 'lsr_lo' || k === 'leverage' || k === 'ratio' || k === 'volume_ratio') return 'x';
  if (unit === 'percent') return '%';
  if (unit === 'rate') return '%';
  if (unit === 'usd' || unit === 'price' || unit === 'unitless' || unit === 'pp' || unit === 'count') return '';
  return ` ${unit}`;
}

/** R04: time with "unknown" fallback instead of raw — missing/invalid stays honest.
 *  按用户设置时区显示（原 toLocaleString 依赖浏览器/SSR 时区，与设置时区不一致）。 */
function fmtTime(ts: number | string | undefined | null, lang: string, tz: string): string {
  const n = typeof ts === 'string' ? Number(ts) : ts;
  if (n == null || Number.isNaN(n) || n <= 0) return lang !== 'en' ? '更新时间未知' : 'Update time unknown';
  return formatTimeInTz(n, tz, lang !== 'en', true);
}

/** D05: narrative 内嵌的时间是 UTC（引擎冻结时生成），渲染时转换为用户设置时区，
 *  使「正文时间」与「详情触发时间」显示一致，不再出现 07:49 / 15:49 的 8 小时差。 */
function localizeNarrative(text: string | undefined, lang: string, tz: string): string {
  if (!text) return '';
  const re = /\b(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2})\b/g;
  return text.replace(re, (m, y, mo, d, h, mi) => {
    const ts = Date.UTC(Number(y), Number(mo) - 1, Number(d), Number(h), Number(mi));
    return formatTimeInTz(ts, tz, lang !== 'en');
  });
}

/** D05: rate (proportion) → percent text with enough decimals; tiny values
 *  degrade to "<0.0001%" instead of a misleading "0.0000%". */
function fmtRatePct(n: number): string {
  const pct = n * 100;
  if (Math.abs(pct) < 0.0001) return '<0.0001%';
  const s = pct.toFixed(6);
  return `${Number(s)}%`;
}

/** 将内部 instrumentId(venue:product:symbol)格式化为友好展示 */
function fmtInstrument(instrumentId: string, zh: boolean): string {
  const [venue, product, ...rest] = instrumentId.split(':');
  const symbol = rest.join(':') || instrumentId;
  const venueName = (venue || '').toUpperCase() || '—';
  const productName = product === 'perp' ? (zh ? '合约' : 'Perp') : product === 'spot' ? (zh ? '现货' : 'Spot') : product || '—';
  return `${venueName} · ${productName} · ${symbol}`;
}

/** D05: 卡片短身份「Binance·合约」，不占宽。已废弃：信号卡片只显示币对标签。 */
const OP_LABEL: Record<string, string> = {
  gt: '>', gte: '≥', lt: '<', lte: '≤', between: '∈', cross_above: '上穿', cross_below: '下穿',
};

/** 指标/阈值字段名 → 友好显示(双语)。key 覆盖信号引擎全部 metric key。 */
const METRIC_LABELS: Record<string, { zh: string; en: string }> = {
  price: { zh: '价格', en: 'Price' },
  change_pct: { zh: '涨跌幅', en: 'Change %' },
  price_change_pct: { zh: '价格变动', en: 'Price change %' },
  quote_volume: { zh: '成交额', en: 'Volume' },
  volume_5m: { zh: '5 分钟成交额', en: 'Volume (5m)' },
  volume_ratio: { zh: '成交量倍数', en: 'Vol ratio' },
  funding_rate: { zh: '资金费率', en: 'Funding rate' },
  prev_rate: { zh: '前次费率', en: 'Prev rate' },
  rsi: { zh: 'RSI', en: 'RSI' },
  volatility: { zh: '波动率', en: 'Volatility' },
  oi_change_pct: { zh: '持仓量变动', en: 'OI change %' },
  depth_drop_pct: { zh: '深度下降', en: 'Depth drop %' },
  basis_pct: { zh: '期现价差', en: 'Basis %' },
  lsr: { zh: '多空比', en: 'Long/Short ratio' },
  lsr_lo: { zh: '多空比(低)', en: 'LSR (low)' },
  leverage: { zh: '杠杆率', en: 'Leverage' },
  ratio: { zh: '盘口比值', en: 'Orderbook ratio' },
  liq_total: { zh: '爆仓总额', en: 'Total liq.' },
  liq_long: { zh: '多单爆仓', en: 'Long liq.' },
  liq_short: { zh: '空单爆仓', en: 'Short liq.' },
  liq_value: { zh: '爆仓金额', en: 'Liq. value' },
  dist_pct: { zh: '距爆仓区', en: 'Dist. to liq.' },
  hist_cross: { zh: 'MACD 柱差', en: 'MACD hist' },
  hist_prev: { zh: 'MACD 前柱', en: 'MACD hist (prev)' },
  hist_cur: { zh: 'MACD 当前柱', en: 'MACD hist (cur)' },
  high_24h: { zh: '24H 高点', en: '24H high' },
  low_24h: { zh: '24H 低点', en: '24H low' },
  trade_value: { zh: '大单金额', en: 'Trade value' },
  macd: { zh: 'MACD', en: 'MACD' },
  macd_signal: { zh: 'MACD 信号线', en: 'MACD signal' },
  macd_hist: { zh: 'MACD 柱', en: 'MACD hist' },
  ema_fast: { zh: 'EMA 快线', en: 'EMA fast' },
  ema_slow: { zh: 'EMA 慢线', en: 'EMA slow' },
  mark_price: { zh: '标记价格', en: 'Mark price' },
  index_price: { zh: '指数价格', en: 'Index price' },
  amount: { zh: '金额', en: 'Amount' },
  count: { zh: '次数', en: 'Count' },
  side: { zh: '方向', en: 'Side' },
  oi: { zh: '持仓量', en: 'OI' },
};

/** 兜底:把 snake_case 字段名转成可读英文(如 macd_death_cross → MACD death cross) */
function humanizeKey(k: string, zh: boolean): string {
  if (zh) return k;
  return k
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function fmtMetricLabel(k: string, zh: boolean): string {
  return METRIC_LABELS[k]?.[zh ? 'zh' : 'en'] ?? humanizeKey(k, zh);
}

/** 信号类型 → 友好标签;未知类型兜底转可读名,绝不裸漏 DB key */
function fmtTypeLabel(t: string, zh: boolean): string {
  const m = TYPE_META[t];
  if (m) return zh ? m.label : m.en;
  return humanizeKey(t, zh);
}

/** en: 卡片描述行 — 用结构化字段重新拼装英文，替代后端中文 detail（严格跟随语言）。 */
function fmtDetailEn(s: MarketSignal, tz: string): string {
  const type = TYPE_META[s.type]?.en ?? humanizeKey(s.type, false);
  const mv = s.metricValues ?? {};
  const th = s.thresholds ?? {};
  const first = Object.entries(mv)[0];
  let body = `${s.symbol} ${type}`;
  if (first) {
    const [k, v] = first;
    const label = fmtMetricLabel(k, false);
    const vstr = fmtMetricValue(s.type, k, v);
    const t0 = Object.entries(th)[0];
    if (t0) {
      const [, tv] = t0;
      const opEn = OP_EN[tv.operator] ?? tv.operator;
      const unit = fmtThresholdUnit(k, tv.unit);
      body += `: ${label} ${vstr} ${opEn} ${tv.value}${unit}`;
    } else {
      body += `: ${label} ${vstr}`;
    }
  }
  const src = s.venue ? s.venue.toUpperCase() : '—';
  body += ` · ${src} · triggered ${formatTimeInTz(s.time, tz, false)}`;
  return body;
}

/** en: 证据抽屉长叙事 — 结构化拼装英文（时间/来源/全部指标），不显示后端中文 narrative。 */
function fmtNarrativeEn(s: MarketSignal, tz: string): string {
  const type = TYPE_META[s.type]?.en ?? humanizeKey(s.type, false);
  const mv = s.metricValues ?? {};
  const th = s.thresholds ?? {};
  const parts = Object.entries(mv).map(([k, v]) => {
    const label = fmtMetricLabel(k, false);
    const vstr = fmtMetricValue(s.type, k, v);
    const tv = th[k];
    if (tv) {
      const opEn = OP_EN[tv.operator] ?? tv.operator;
      const unit = fmtThresholdUnit(k, tv.unit);
      return `${label} ${vstr} ${opEn} ${tv.value}${unit}`;
    }
    return `${label} ${vstr}`;
  });
  const cond = parts.length ? parts.join('; ') : type;
  return `${s.symbol} ${type} — ${cond}. Source ${s.venue ?? '—'}; triggered ${formatTimeInTz(s.time, tz, false)}.`;
}

/** 操作符 → 英文 */
const OP_EN: Record<string, string> = {
  '>': 'above', gte: '≥', '<': 'below', lte: '≤', between: 'between', cross_above: 'crossed above', cross_below: 'crossed below', '=': '=', in: 'in',
};

export default function SignalsPage() {
  const { language, user } = useAppStore();
  const authReady = useRequireAuth('/signals', true);
  const { timezone: tzPref } = useDataSettings();
  const t = translate(language);
  const zh = language !== 'en';
  const [cat, setCat] = useState<SignalCategory>('all');

  // ── 筛选（AUD07 history filters） ──────────────────────────────
  const [fSymbol, setFSymbol] = useState('');
  const [fExact, setFExact] = useState(false);
  const [fProduct, setFProduct] = useState('');
  const [fVenue, setFVenue] = useState('');
  const [fFrom, setFFrom] = useState('');
  const [fTo, setFTo] = useState('');
  const [favOnly, setFavOnly] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);

  // ── 分页（cursor） ────────────────────────────────────────────
  const [items, setItems] = useState<MarketSignal[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(false);
  const [appliedQuery, setAppliedQuery] = useState('');
  const listSeq = useRef(0);

  const queryString = useMemo(() => {
    const p = new URLSearchParams({ limit: '30' });
    if (cat !== 'all') p.set('type', cat);
    if (fSymbol.trim()) {
      p.set('symbol', fSymbol.trim().toUpperCase());
      if (fExact) p.set('exact', '1');
    }
    if (fProduct) p.set('product', fProduct);
    if (fVenue) p.set('venue', fVenue);
    if (fFrom) p.set('from', String(new Date(fFrom).getTime()));
    if (fTo) p.set('to', String(new Date(fTo).getTime() + 86399999));
    if (favOnly && user) p.set('fav', '1');
    return p.toString();
  }, [cat, fSymbol, fProduct, fVenue, fFrom, fTo, favOnly, user]);

  const loadFirst = async () => {
    const seq = ++listSeq.current;
    setLoading(true);
    setError(false);
    setAppliedQuery(queryString);
    try {
      const res = await api.get<SignalListResponse>(`/market/signals?${queryString}`);
      if (seq !== listSeq.current) return;
      setItems(res.items ?? []);
      setNextCursor(res.nextCursor ?? null);
      setTotal(res.total ?? 0);
      if (res.counts) setCounts(res.counts);
    } catch {
      if (seq === listSeq.current) setError(true);
    } finally {
      if (seq === listSeq.current) setLoading(false);
    }
  };

  const loadMore = async () => {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const res = await api.get<SignalListResponse>(`/market/signals?${appliedQuery}&cursor=${encodeURIComponent(nextCursor)}`);
      setItems((p) => {
        const seen = new Set(p.map((s) => s.id));
        return [...p, ...(res.items ?? []).filter((s) => !seen.has(s.id))];
      });
      setNextCursor(res.nextCursor ?? null);
    } catch { /* keep current list */ } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    loadFirst();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryString]);

  // ── 实时新增缓存（N new pill） ─────────────────────────────────
  const [newCount, setNewCount] = useState(0);
  const firstLoaded = useRef(false);
  useEffect(() => {
    if (!firstLoaded.current) { firstLoaded.current = true; return; }
    const iv = setInterval(async () => {
      try {
        const res = await api.get<SignalListResponse>(`/market/signals?limit=5${cat !== 'all' ? `&type=${cat}` : ''}`);
        const newIds = (res.items ?? []).filter((s) => !items.some((x) => x.id === s.id));
        if (newIds.length) setNewCount((n) => n + newIds.length);
      } catch { /* ignore */ }
    }, 30000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length, cat]);

  const revealNew = () => { setNewCount(0); loadFirst(); };

  // ── 收藏 / 笔记 ───────────────────────────────────────────────
  const [noteDraft, setNoteDraft] = useState('');
  const [noteFor, setNoteFor] = useState<string | null>(null);
  const [favBusy, setFavBusy] = useState<string | null>(null);
  const [noteBusy, setNoteBusy] = useState(false);
  const [toast, setToast] = useState('');
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(''), 2500);
    return () => clearTimeout(t);
  }, [toast]);

  const toggleFav = async (s: MarketSignal) => {
    if (!user) { window.location.href = '/login?redirect=/signals'; return; }
    setFavBusy(s.id);
    try {
      if (s._fav) await api.delete(`/market/signals/${encodeURIComponent(s.snapshotId || s.id)}/favorite`);
      else await api.post(`/market/signals/${encodeURIComponent(s.snapshotId || s.id)}/favorite`);
      setItems((p) => p.map((x) => (x.id === s.id ? { ...x, _fav: !x._fav } : x)));
    } catch { /* ignore */ } finally {
      setFavBusy(null);
    }
  };

  const openNoteFor = (s: MarketSignal) => { setNoteFor(s.id); setNoteDraft(s._note || ''); };

  /** D20: 从信号保存的笔记写入工作台研究笔记（自动关联该信号，可在工作台打开同一信号）。 */
  const saveNote = async (s: MarketSignal) => {
    if (!user) { window.location.href = '/login?redirect=/signals'; return; }
    const content = noteDraft.trim();
    if (!content) return;
    setNoteBusy(true);
    try {
      await api.post('/workspace/notes', {
        targetType: 'signal',
        targetId: s.snapshotId || s.id,
        snapshot: { name: `${s.type} · ${s.symbol}` },
        content,
      });
      setItems((p) => p.map((x) => (x.id === s.id ? { ...x, _note: noteDraft } : x)));
      setNoteFor(null);
      setNoteDraft('');
      setToast(zh ? '已储存至工作台笔记' : 'Saved to workspace notes');
    } catch { setToast(zh ? '储存失败' : 'Save failed'); } finally {
      setNoteBusy(false);
    }
  };

  // ── 证据抽屉 ──────────────────────────────────────────────────
  const [evidenceFor, setEvidenceFor] = useState<MarketSignal | null>(null);
  useEffect(() => {
    if (evidenceFor) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
  }, [evidenceFor]);

  const triggerText = (s: MarketSignal): string => {
    const parts: string[] = [];
    if (s.changePct !== 0) parts.push(`${s.changePct >= 0 ? '+' : ''}${s.changePct.toFixed(2)}%`);
    if (s.fundingRate != null) parts.push(`${(s.fundingRate * 100).toFixed(4)}%`);
    if (s.oiChangePct != null) parts.push(`OI ${s.oiChangePct >= 0 ? '+' : ''}${s.oiChangePct.toFixed(1)}%`);
    if (s.type === 'volume_spike' && s.volume5mUsd != null && s.volume5mUsd > 0) parts.push(`${zh ? '5m 成交额' : '5m vol'} ${formatVolume(s.volume5mUsd)}`);
    else if (s.volumeUsd != null && s.volumeUsd > 0) parts.push(formatVolume(s.volumeUsd));
    return parts.join(' · ') || '—';
  };

  /** R04: metric-aware primary badge — reads the frozen snapshot fields so the
   *  card never shows a fake "涨跌幅" for RSI/LSR/volatility signals. */
  const metricBadge = (s: MarketSignal) => {
    const parts: Array<{ v: string; cls: string }> = [];
    const mv = s.metricValues ?? {};
    const push = (v: string, cls: string) => parts.push({ v, cls });
    if (s.type.startsWith('rsi_') && mv.rsi?.value != null) {
      push(`RSI ${mv.rsi.value.toFixed(1)}`, mv.rsi.value <= 30 ? 'text-gf-positive' : 'text-gf-negative');
    } else if (s.type === 'lsr_extreme' && mv.lsr?.value != null) {
      push(`${zh ? '多空比' : 'LSR'} ${mv.lsr.value.toFixed(2)}x`, 'text-gf-muted');
    } else if (s.type === 'leverage_surge' && mv.leverage?.value != null) {
      push(`${zh ? '杠杆' : 'Lev'} ${mv.leverage.value.toFixed(2)}x`, 'text-gf-muted');
    } else if (s.type === 'volatility_surge' && mv.volatility?.value != null) {
      push(`${zh ? '波动' : 'Vol'} ${mv.volatility.value.toFixed(0)}%`, 'text-gf-negative');
    } else if ((s.type === 'oi_drop' || s.type === 'oi_spike' || s.type === 'oi_price_divergence') && mv.oi_change_pct?.value != null) {
      const v = mv.oi_change_pct.value;
      push(`OI ${v >= 0 ? '+' : ''}${v.toFixed(1)}%`, v >= 0 ? 'text-gf-negative' : 'text-gf-positive');
    } else if ((s.type === 'liq_surge' || s.type === 'long_liq_cascade' || s.type === 'short_liq_cascade' || s.type === 'liq_cluster_near') && (mv.liq_total?.value ?? mv.liq_long?.value ?? mv.liq_short?.value ?? mv.liq_value?.value) != null) {
      push(formatVolume((mv.liq_total?.value ?? mv.liq_long?.value ?? mv.liq_short?.value ?? mv.liq_value?.value) as number), 'text-gf-negative');
    } else if (s.type === 'funding_extreme' && s.fundingRate != null) {
      push(`${(s.fundingRate * 100).toFixed(4)}%`, s.fundingRate >= 0 ? 'text-gf-positive' : 'text-gf-negative');
    } else if (s.type === 'funding_reversal' && mv.funding_rate?.value != null && mv.prev_rate?.value != null) {
      push(`${fmtRatePct(mv.prev_rate.value)} → ${fmtRatePct(mv.funding_rate.value)}`, 'text-gf-muted');
    } else if (s.type === 'volume_spike') {
      const v5 = s.volume5mUsd ?? (mv.volume_5m?.value != null ? Number(mv.volume_5m.value) : null);
      if (v5 != null && v5 > 0) push(`${zh ? '5m 成交额' : '5m vol'} ${formatVolume(v5)}`, 'text-gf-muted');
    } else {
      if (s.changePct !== 0) push(`${s.changePct >= 0 ? '+' : ''}${s.changePct.toFixed(2)}%`, s.changePct >= 0 ? 'text-gf-positive' : 'text-gf-negative');
      if (s.fundingRate != null) push(`${(s.fundingRate * 100).toFixed(4)}%`, s.fundingRate >= 0 ? 'text-gf-positive' : 'text-gf-negative');
    }
    return parts;
  };

  const filtered = items; // 分类筛选已由后端 type 参数处理

  // 分类徽标计数:优先用后端全库分类计数,缺失时降级为当页统计
  const categoryCounts = useMemo(() => {
    if (counts && Object.keys(counts).length) return counts;
    const acc: Record<string, number> = {};
    items.forEach((s) => {
      const c = TYPE_META[s.type]?.cat ?? 'price';
      acc[c] = (acc[c] ?? 0) + 1;
    });
    return acc;
  }, [counts, items]);

  // 抽屉渲染：按触发时间/规则条件/当时数值/阈值/窗口/来源/质量排序
  const evidenceDrawer = evidenceFor ? (
    <div className="fixed inset-0 z-[80] flex justify-end">
      <div className="absolute inset-0 bg-black/40" onClick={() => setEvidenceFor(null)} />
      {/* 桌面 480px 抽屉 / 手机全屏 */}
      <div className="relative flex h-full w-full flex-col bg-gf-bg shadow-2xl sm:max-w-[480px] sm:border-l sm:border-gf-border">
        <div className="flex items-center justify-between border-b border-gf-border px-4 py-3">
          <div className="flex items-center gap-2">
            <span className={`rounded-md border px-2 py-0.5 text-xs font-medium ${TYPE_META[evidenceFor.type]?.cls ?? 'bg-gf-surface2 text-gf-muted border-gf-border'}`}>
              {fmtTypeLabel(evidenceFor.type, zh)}
            </span>
            <span className="font-semibold">{evidenceFor.symbol}</span>
          </div>
          <button className="rounded-md px-2 py-1 text-xs text-gf-muted hover:text-gf-text" onClick={() => setEvidenceFor(null)}>
            ✕
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          {/* 标题：币种和事件 */}
          <h2 className="text-lg font-bold">
            {evidenceFor.symbol} · {fmtTypeLabel(evidenceFor.type, zh)}
          </h2>
          <p className="mt-0.5 text-xs text-gf-muted">{zh ? evidenceFor.detail : fmtDetailEn(evidenceFor, tzPref)}</p>

          {/* N02：触发时点回放入口 */}
          {evidenceFor.instrumentId && evidenceFor.time ? (
            <Link
              href={`/replay?instrumentId=${encodeURIComponent(evidenceFor.instrumentId)}&eventTime=${evidenceFor.time}&window=30&interval=5m${evidenceFor.snapshotId ? `&evidenceRef=${encodeURIComponent(evidenceFor.snapshotId)}` : ''}&symbol=${encodeURIComponent(evidenceFor.symbol)}&type=${encodeURIComponent(evidenceFor.type)}`}
              className="mt-3 flex items-center justify-center gap-1.5 rounded-lg bg-gf-accent px-3 py-2 text-sm font-medium text-white transition-opacity hover:opacity-90"
            >
              {zh ? '触发时点回放' : 'Replay at trigger time'}
            </Link>
          ) : null}

          {/* 触发时间 / 规则版本 / 来源 / 质量 */}
          <div className="mt-3 grid grid-cols-2 gap-1.5 text-[11px]">
            <div className="rounded-lg border border-gf-border/60 bg-gf-surface2/50 p-2">
              <div className="text-gf-muted">{zh ? '触发时间' : 'Triggered'}</div>
              <div className="mt-0.5 font-medium">
                {fmtTime(evidenceFor.time ?? evidenceFor.availableAt, language, tzPref)}
              </div>
            </div>
            <div className="rounded-lg border border-gf-border/60 bg-gf-surface2/50 p-2">
              <div className="text-gf-muted">{zh ? '规则版本' : 'Rule version'}</div>
              <div className="mt-0.5 font-medium">v{evidenceFor.ruleVersion ?? 1}</div>
            </div>
            <div className="rounded-lg border border-gf-border/60 bg-gf-surface2/50 p-2">
              <div className="text-gf-muted">{zh ? '来源' : 'Source'}</div>
              <div className="mt-0.5 font-medium">{evidenceFor.source ?? evidenceFor.venue ?? '—'}</div>
            </div>
            <div className="rounded-lg border border-gf-border/60 bg-gf-surface2/50 p-2">
              <div className="text-gf-muted">{zh ? '质量' : 'Quality'}</div>
              <div className="mt-0.5">
                {evidenceFor.quality ? (
                  <DataQualityBadge quality={evidenceFor.quality as 'ok' | 'partial' | 'stale' | 'missing' | 'unsupported'} asOf={evidenceFor.availableAt ?? evidenceFor.time} />
                ) : '—'}
              </div>
            </div>
            {evidenceFor.instrumentId && (
              <div className="col-span-2 rounded-lg border border-gf-border/60 bg-gf-surface2/50 p-2">
                <div className="text-gf-muted">{zh ? '标的' : 'Instrument'}</div>
                <div className="mt-0.5 text-[11px]">{fmtInstrument(evidenceFor.instrumentId, zh)}</div>
              </div>
            )}
          </div>

          {/* 可验证叙述 */}
          {evidenceFor.narrative && (
            <div className="mt-3 rounded-lg border border-gf-accent/30 bg-gf-accent/5 p-2.5">
              <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-gf-accent">
                {zh ? '可验证叙述' : 'Verifiable narrative'}
              </div>
              <p className="text-xs leading-relaxed text-gf-text">{zh ? localizeNarrative(evidenceFor.narrative, language, tzPref) : fmtNarrativeEn(evidenceFor, tzPref)}</p>
            </div>
          )}

          {/* 规则条件（阈值） */}
          {evidenceFor.thresholds && Object.keys(evidenceFor.thresholds).length > 0 && (
            <div className="mt-3">
              <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-gf-muted">
                {zh ? '规则条件 / 阈值' : 'Rule conditions / thresholds'}
              </div>
              <div className="space-y-1">
                {Object.entries(evidenceFor.thresholds).map(([k, th]) => (
                  <div key={k} className="flex items-center justify-between rounded-lg border border-gf-border/60 bg-gf-surface2/50 px-2.5 py-1.5 text-[11px]">
                    <span>{fmtMetricLabel(k, zh)}</span>
                    <span className="text-gf-muted">
                      {OP_LABEL[th.operator] ?? th.operator} {th.value}
                      {fmtThresholdUnit(k, th.unit)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 触发时数值快照 */}
          {evidenceFor.metricValues && Object.keys(evidenceFor.metricValues).length > 0 && (
            <div className="mt-3">
              <div className="mb-1 flex items-baseline justify-between text-[10px] font-semibold uppercase tracking-wide">
                <span className="text-gf-muted">{zh ? '触发时数值' : 'Values at trigger'}</span>
                {evidenceFor.currentPrice != null && evidenceFor.currentPrice > 0 && evidenceFor.currentPrice !== evidenceFor.price && (
                  <span className="normal-case tracking-normal text-gf-accent">
                    {zh ? '现价' : 'Now'} <span className="font-mono text-[11px]">{formatPrice(evidenceFor.currentPrice)}</span>
                  </span>
                )}
              </div>
              <div className="space-y-1">
                {Object.entries(evidenceFor.metricValues).map(([k, mv]) => (
                  <div key={k} className="flex items-center justify-between rounded-lg border border-gf-border/60 bg-gf-surface2/50 px-2.5 py-1.5 text-[11px]">
                    <span>{fmtMetricLabel(k, zh)}</span>
                    <span className="flex items-center gap-2">
                      {mv.window && <span className="text-[10px] text-gf-muted">{mv.window}</span>}
                      <span className="font-bold tabular-nums">
                        {fmtMetricValue(evidenceFor.type, k, mv)}
                      </span>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 收藏 / 笔记（登录后） */}
          {user && (
            <div className="mt-4 space-y-2 border-t border-gf-border pt-3">
              <button
                onClick={() => toggleFav(evidenceFor)}
                disabled={favBusy === evidenceFor.id}
                className={`flex w-full items-center justify-center gap-1.5 rounded-lg border px-3 py-2 text-xs transition-colors ${
                  evidenceFor._fav ? 'border-gf-accent/50 bg-gf-accent/15 text-gf-accent' : 'border-gf-border bg-gf-surface2 text-gf-muted hover:text-gf-accent'
                }`}
              >
                {evidenceFor._fav ? '★ ' : '☆ '}{zh ? '已收藏' : 'Favorite'}
              </button>
              {noteFor === evidenceFor.id ? (
                <div className="space-y-1.5">
                  <textarea
                    value={noteDraft}
                    onChange={(e) => setNoteDraft(e.target.value)}
                    rows={3}
                    maxLength={5000}
                    placeholder={zh ? '笔记会储存至工作台（观点/依据/失效条件/后续观察）' : 'Note saved to workspace (view, evidence, invalidation, follow-ups)'}
                    className="input w-full !py-2 text-xs"
                  />
                  <div className="flex gap-1.5">
                    <button className="btn-primary flex-1 !py-1.5 text-xs" onClick={() => saveNote(evidenceFor)} disabled={noteBusy}>
                      {noteBusy ? '…' : zh ? '保存' : 'Save'}
                    </button>
                    <button className="btn flex-1 !py-1.5 text-xs" onClick={() => { setNoteFor(null); setNoteDraft(''); }}>
                      {zh ? '取消' : 'Cancel'}
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => { setNoteFor(evidenceFor.id); setNoteDraft(evidenceFor._note || ''); }}
                  className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-gf-border bg-gf-surface2 px-3 py-2 text-xs text-gf-muted transition-colors hover:text-gf-accent"
                >
                  {evidenceFor._note ? `✎ ${zh ? '编辑笔记' : 'Edit note'}` : `✎ ${zh ? '添加笔记' : 'Add note'}`}
                </button>
              )}
              {evidenceFor._note && noteFor !== evidenceFor.id && (
                <div className="rounded-lg border border-gf-border/60 bg-gf-surface2/40 px-2.5 py-2 text-[11px] text-gf-muted">
                  {evidenceFor._note}
                </div>
              )}
            </div>
          )}

          <button
            className="mt-4 w-full rounded-lg border border-gf-border bg-gf-surface2 py-2 text-xs text-gf-muted hover:text-gf-accent"
            onClick={() =>
              (window.location.href =
                buildCoinRoute(
                  parseInstrumentId(evidenceFor.instrumentId) ?? {
                    symbol: evidenceFor.symbol,
                    exchange: evidenceFor.source ?? evidenceFor.venue,
                    product: evidenceFor.product as InstrumentIdentity['product'],
                  },
                ))
            }
          >
            {zh ? '查看币种行情 →' : 'View coin market →'}
          </button>
        </div>
      </div>
    </div>
  ) : null;

  
  const exportCsv = () => {
    if (items.length === 0) return;
  const headers = zh ? ['类型', '币种', '价格', '24H涨跌', '资金费率', 'OI变化', '时间'] : ['Type', 'Symbol', 'Price', '24H Change', 'Funding Rate', 'OI Change', 'Time'];
  const typeLabel = (t: unknown) => {
    const m = TYPE_META[String(t ?? '')];
    return m ? (zh ? m.label : m.en) : String(t ?? '');
  };
  const data = (items as unknown as Record<string, unknown>[]).map((it) => [
    typeLabel(it.type), String(it.symbol ?? ''), String(it.current_price ?? it.currentPrice ?? it.price ?? ''), String(it.change_pct ?? it.changePct ?? ''),
    it.funding_rate != null ? (Number(it.funding_rate) * 100).toFixed(4) + '%' : it.fundingRate != null ? (Number(it.fundingRate) * 100).toFixed(4) + '%' : '',
    String(it.oi_change_pct ?? it.oiChangePct ?? ''), String(it.time ?? it.triggered_at ?? it.created_at ?? it.triggeredAt ?? it.createdAt ?? '').slice(0, 19),
  ]);
  downloadCsv(`ailumix-signals-${new Date().toISOString().slice(0, 10)}.csv`, headers, data);
};
  return (
    <div>
      <EventPing event="signal_view" />
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-2xl font-bold">{zh ? '市场信号' : 'Market Signals'}</h1>
          <p className="mt-0.5 text-xs text-gf-muted">
            {zh ? '实时监测爆仓、资金费率、持仓、技术指标等市场异动' : 'Real-time market anomaly detection'}
          </p>
        </div>
        <div className="flex items-center gap-2 text-xs text-gf-muted">
          {loading ? (
            <span className="inline-flex items-center gap-1">
              <span className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-gf-accent" />
              {zh ? '更新中' : 'updating'}
            </span>
          ) : (
            <span>{zh ? `${items.length}/${total} 条` : `${items.length}/${total}`}</span>
          )}
        </div>
      </div>

      {/* 分类 tabs */}
      <div className="mb-1.5 flex items-center justify-end">
        <button
          type="button"
          onClick={exportCsv}
          className="rounded-full border border-gf-border bg-gf-surface2 px-2.5 py-1 text-[11px] text-gf-muted transition-colors hover:border-gf-accent/40 hover:text-gf-text sm:px-3 sm:text-xs"
        >
          {zh ? '导出 CSV' : 'Export CSV'}
        </button>
      </div>
      <div className="mb-3 flex gap-1.5 overflow-x-auto whitespace-nowrap pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {CATEGORIES.map((c) => (
          <button
            key={c.key}
            onClick={() => setCat(c.key)}
            className={`flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs transition-colors ${
              cat === c.key
                ? 'border-gf-accent bg-gf-accent/15 text-gf-accent'
                : 'border-gf-border bg-gf-surface2 text-gf-muted hover:text-gf-text'
            }`}
          >
            {zh ? c.label : c.en}
            {c.key !== 'all' && categoryCounts[c.key] > 0 && (
              <span className={`rounded-full px-1.5 py-0.5 text-[10px] ${cat === c.key ? 'bg-gf-accent/20' : 'bg-gf-border/50'}`}>
                {categoryCounts[c.key]}
              </span>
            )}
          </button>
        ))}
        <button
          onClick={() => setFilterOpen((v) => !v)}
          className={`flex shrink-0 items-center gap-1 rounded-full border px-3 py-1.5 text-xs transition-colors ${
            filterOpen ? 'border-gf-accent bg-gf-accent/15 text-gf-accent' : 'border-gf-border bg-gf-surface2 text-gf-muted hover:text-gf-text'
          }`}
        >
          ⚙ {zh ? '筛选' : 'Filter'}
          {(fSymbol || fProduct || fVenue || fFrom || fTo || favOnly) && (
            <span className="rounded-full bg-gf-accent/20 px-1.5 text-[10px] text-gf-accent">●</span>
          )}
        </button>
      </div>

      {/* 筛选面板 */}
      {filterOpen && (
        <div className="mb-3 grid grid-cols-2 gap-2 rounded-lg border border-gf-border/70 bg-gf-surface2/40 p-3 sm:grid-cols-3 lg:grid-cols-6">
          <div>
            <label className="mb-1 block text-[10px] text-gf-muted">{zh ? '币种' : 'Symbol'}</label>
            <input
              value={fSymbol}
              onChange={(e) => setFSymbol(e.target.value)}
              placeholder="BTCUSDT"
              className="input !py-1.5 text-xs"
            />
            <label className="mt-1.5 flex cursor-pointer items-center gap-1.5 text-[10px] text-gf-muted">
              <input type="checkbox" checked={fExact} onChange={(e) => setFExact(e.target.checked)} className="accent-gf-accent" />
              {zh ? '精确匹配（避免混入 PUMPBTCUSDT 等）' : 'Exact match (exclude PUMPBTCUSDT etc.)'}
            </label>
          </div>
          <div>
            <label className="mb-1 block text-[10px] text-gf-muted">{zh ? '产品' : 'Product'}</label>
            <select value={fProduct} onChange={(e) => setFProduct(e.target.value)} className="input !py-1.5 text-xs">
              <option value="">{zh ? '全部' : 'All'}</option>
              <option value="spot">{zh ? '现货' : 'Spot'}</option>
              <option value="perp">{zh ? '合约' : 'Perp'}</option>
            </select>
          </div>
          <div>
            <label className="mb-1 block text-[10px] text-gf-muted">{zh ? '交易所' : 'Exchange'}</label>
            <select value={fVenue} onChange={(e) => setFVenue(e.target.value)} className="input !py-1.5 text-xs">
              {VENUES.map((v) => (
                <option key={v} value={v}>{v === '' ? (zh ? '全部' : 'All') : v.toUpperCase()}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-[10px] text-gf-muted">{zh ? '开始' : 'From'}</label>
            <input type="date" value={fFrom} onChange={(e) => setFFrom(e.target.value)} className="input !py-1.5 text-xs" />
          </div>
          <div>
            <label className="mb-1 block text-[10px] text-gf-muted">{zh ? '结束' : 'To'}</label>
            <input type="date" value={fTo} onChange={(e) => setFTo(e.target.value)} className="input !py-1.5 text-xs" />
          </div>
          <div className="flex flex-col justify-end">
            <button
              onClick={() => { setFavOnly((v) => !v); if (!user) window.location.href = '/login?redirect=/signals'; }}
              className={`rounded-lg border px-2 py-1.5 text-[11px] transition-colors ${
                favOnly ? 'border-gf-accent bg-gf-accent/15 text-gf-accent' : 'border-gf-border bg-gf-surface2 text-gf-muted'
              }`}
            >
              {favOnly ? '★ ' : '☆ '}{zh ? '仅收藏' : 'Favorites only'}
            </button>
          </div>
        </div>
      )}

      {/* N new pill */}
      {newCount > 0 && (
        <button
          onClick={revealNew}
          className="mb-3 flex w-full items-center justify-center gap-2 rounded-lg border border-gf-accent/40 bg-gf-accent/10 py-1.5 text-xs font-medium text-gf-accent transition-colors hover:bg-gf-accent/20"
        >
          <span className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-gf-accent" />
          {zh ? `新增 ${newCount} 条信号，点击查看` : `${newCount} new signal${newCount > 1 ? 's' : ''} — tap to view`}
        </button>
      )}

      {/* 列表 */}
      <div className="flex flex-col gap-2">
        {error && !items.length ? (
          <div className="card p-8 text-center text-sm text-gf-negative">{zh ? '载入失败，请重试' : 'Failed to load — retry'}</div>
        ) : filtered.length === 0 && !loading ? (
          <div className="card p-8 text-center text-sm text-gf-muted">
            {zh ? '暂无符合条件的信号' : 'No matching signals'}
          </div>
        ) : (
          filtered.map((s) => {
            const m = TYPE_META[s.type] ?? { label: fmtTypeLabel(s.type, true), en: fmtTypeLabel(s.type, false), cls: 'bg-gf-surface2 text-gf-muted border-gf-border', cat: 'price' as SignalCategory };
            const metrics = metricBadge(s);
            return (
              <div key={s.id}>
                {/* 手机卡 */}
                <div className="card cursor-pointer !py-3 transition-colors hover:border-gf-accent/50 sm:hidden" onClick={() => setEvidenceFor(s)}>
                  <div className="flex items-center gap-2">
                    <span className={`shrink-0 rounded-md border px-2 py-0.5 text-xs font-medium ${m.cls}`}>{zh ? m.label : m.en}</span>
{(() => { const p2 = signalPlain(s.type, (language as TermLang)); return p2 && p2.tip ? <TermTip term={p2.tip} /> : null })()}
                    <span className="min-w-0 flex-1 truncate font-semibold">{s.symbol}</span>
                    {s._fav && <span className="shrink-0 text-[10px] text-gf-accent">★</span>}
                    {s._note && <span className="shrink-0 text-[10px] text-gf-muted">✎</span>}
                    <span className="ml-auto shrink-0 text-right">
                      <span className="block whitespace-nowrap font-mono text-sm">{formatPrice(s.price)}</span>
                      <span className="block text-[9px] text-gf-muted">{zh ? '触发价' : 'Trigger'}</span>
                      {s.currentPrice != null && s.currentPrice > 0 && s.currentPrice !== s.price && (
                        <span className="block whitespace-nowrap text-[9px] text-gf-accent/90">
                          {zh ? '现价' : 'Now'} <span className="font-mono text-[11px]">{formatPrice(s.currentPrice)}</span>
                        </span>
                      )}
                    </span>
                  </div>
                  <div className="mt-1.5 flex items-baseline gap-2 text-sm text-gf-muted">
                    {metrics.length > 0 && (
                      <span className={`shrink-0 font-mono text-xs ${metrics[0].cls}`}>{metrics[0].v}</span>
                    )}
                    <span className="min-w-0 flex-1 truncate">{zh ? s.detail : fmtDetailEn(s, tzPref)}</span>
                    <span className="shrink-0 text-xs">{timeAgo(s.time, language)}</span>
{(() => { const p2 = signalPlain(s.type, (language as TermLang)); return p2 ? (<div className="mt-1 border-t border-dashed border-gf-border/60 pt-1 text-[10px] text-gf-muted/80">{p2.text}{p2.tip ? <TermTip term={p2.tip} /> : null}</div>) : null })()}
                  </div>
                  {s.narrative && (
                    <div className="mt-1 truncate border-t border-dashed border-gf-border/60 pt-1 text-[10px] text-gf-muted/80">
                      {zh ? localizeNarrative(s.narrative, language, tzPref) : fmtNarrativeEn(s, tzPref)}
                    </div>
                  )}
                  <div className="mt-2 flex items-center justify-end gap-2">
                    {user && (
                      <button
                        onClick={(e) => { e.stopPropagation(); toggleFav(s); }}
                        disabled={favBusy === s.id}
                        className="rounded-md border border-gf-border bg-gf-surface2 px-2 py-1 text-[10px] text-gf-muted hover:text-gf-accent"
                      >
                        {s._fav ? '★' : '☆'}
                      </button>
                    )}
                    <a
                      href={`/alerts?symbol=${s.symbol}`}
                      onClick={(e) => e.stopPropagation()}
                      className="rounded-md border border-gf-border bg-gf-surface2 px-2 py-1 text-[10px] text-gf-muted hover:text-gf-accent"
                    >
                      {zh ? '设置预警' : 'Set alert'}
                    </a>
                    {user && (
                      <button
                        onClick={(e) => { e.stopPropagation(); openNoteFor(s); }}
                        className="rounded-md border border-gf-border bg-gf-surface2 px-2 py-1 text-[10px] text-gf-muted hover:text-gf-accent"
                      >
                        {zh ? '笔记' : 'Note'}
                      </button>
                    )}
                  </div>
                  {user && noteFor === s.id && (
                    <div className="mt-2 space-y-1.5 border-t border-dashed border-gf-border/60 pt-2" onClick={(e) => e.stopPropagation()}>
                      <textarea
                        value={noteDraft}
                        onChange={(e) => setNoteDraft(e.target.value)}
                        rows={3}
                        maxLength={5000}
                        placeholder={zh ? '笔记会储存至工作台（观点/依据/失效条件/后续观察）' : 'Note saved to workspace (view, evidence, invalidation, follow-ups)'}
                        className="input w-full !py-2 text-xs"
                      />
                      <div className="flex gap-1.5">
                        <button className="btn-primary flex-1 !py-1.5 text-xs" onClick={() => saveNote(s)} disabled={noteBusy}>
                          {noteBusy ? '…' : zh ? '保存' : 'Save'}
                        </button>
                        <button className="btn flex-1 !py-1.5 text-xs" onClick={() => { setNoteFor(null); setNoteDraft(''); }}>
                          {zh ? '取消' : 'Cancel'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                {/* 桌面行 */}
                <div className="card hidden cursor-pointer !py-2.5 px-4 transition-colors hover:border-gf-accent/50 sm:block" onClick={() => setEvidenceFor(s)}>
                  <div className="flex items-center gap-3">
                    <span className={`w-24 shrink-0 rounded-md border px-2 py-0.5 text-center text-xs font-medium ${m.cls}`}>{zh ? m.label : m.en}</span>
{(() => { const p2 = signalPlain(s.type, (language as TermLang)); return p2 && p2.tip ? <TermTip term={p2.tip} /> : null })()}
                    <span className="flex w-24 shrink-0 flex-col items-start gap-0.5 font-semibold">
                      <span className="flex items-center gap-1">
                        {s.symbol}
                        {s._fav && <span className="text-[10px] text-gf-accent">★</span>}
                      </span>
                    </span>
                    <span className="w-24 shrink-0 truncate font-mono text-xs text-gf-text">
                      <span className="mr-1 text-[9px] text-gf-muted">{zh ? '触发' : 'Trig'}</span>
                      <span className="mr-2">{formatPrice(s.price)}</span>
                      {s.currentPrice != null && s.currentPrice > 0 && s.currentPrice !== s.price && (
                        <>
                          <span className="mr-1 text-[9px] text-gf-muted">{zh ? '现价' : 'Now'}</span>
                          <span className="mr-2 text-gf-accent">{formatPrice(s.currentPrice)}</span>
                        </>
                      )}
                      {metrics.map((mt, i) => (
                        <span key={i} className={`mr-2 ${mt.cls}`}>{mt.v}</span>
                      ))}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-sm">{zh ? s.detail : fmtDetailEn(s, tzPref)}</span>
                    <span className="shrink-0 text-xs text-gf-muted">{timeAgo(s.time, language)}</span>
                    <a
                      href={`/alerts?symbol=${s.symbol}`}
                      onClick={(e) => e.stopPropagation()}
                      className="shrink-0 rounded-md border border-gf-border bg-gf-surface2 px-2 py-1 text-[10px] text-gf-muted hover:text-gf-accent"
                    >
                      {zh ? '预警' : 'Alert'}
                    </a>
                    {user && (
                      <button
                        onClick={(e) => { e.stopPropagation(); openNoteFor(s); }}
                        className="shrink-0 rounded-md border border-gf-border bg-gf-surface2 px-2 py-1 text-[10px] text-gf-muted hover:text-gf-accent"
                      >
                        {zh ? '笔记' : 'Note'}
                      </button>
                    )}
                    <span className="shrink-0 rounded-md border border-gf-accent/40 bg-gf-accent/10 px-2 py-1 text-[10px] text-gf-accent">
                      {zh ? '查看依据' : 'Evidence'}
                    </span>
                  </div>
                  {s.narrative && (
                    <div className="mt-1.5 truncate border-t border-dashed border-gf-border/60 pt-1 text-[10px] text-gf-muted/80">
                      {zh ? localizeNarrative(s.narrative, language, tzPref) : fmtNarrativeEn(s, tzPref)}
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
        {loading && !items.length && <ListSkeleton rows={5} />}
        {!loading && !items.length && (
          <EmptyState
            compact
            title={zh ? '暂无信号' : 'No signals yet'}
            description={zh ? '调整筛选条件后再看' : 'Adjust your filters to see signals.'}
          />
        )}
      </div>

      {/* 加载更多 */}
      {nextCursor && (
        <div className="mt-4 flex justify-center">
          <button
            onClick={loadMore}
            disabled={loadingMore}
            className="btn !py-2 text-xs"
          >
            {loadingMore ? (zh ? '载入中…' : 'Loading…') : zh ? '载入更多' : 'Load more'}
          </button>
        </div>
      )}

      {evidenceDrawer}

      {/* toast */}
      {toast && (
        <div className="fixed bottom-20 left-1/2 z-[60] -translate-x-1/2 rounded-lg bg-gf-text px-4 py-2 text-xs font-medium text-gf-surface shadow-lg">
          {toast}
        </div>
      )}
    </div>
  );
}
